import { TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { BrushRange, ChartSelection, MetricConfig, RechartsDataPoint } from "./TrendsOverTimeChart.types";

// ============================================
// Types
// ============================================

/** Unified selection range type (works with both Brush and ReferenceArea) */
export type SelectionRange = BrushRange | ChartSelection | null;

export interface MetricVariation {
  key: string;
  label: string;
  color?: string;
  startValue: number | null;
  endValue: number | null;
  delta: number | null;
  percentChange: number | null;
}

export interface BrushVariationSummaryProps {
  /** Current selection range (from Brush or ReferenceArea) */
  selection: SelectionRange;
  /** Chart data points (row-based) */
  data: RechartsDataPoint[];
  /** Enabled metrics to show variation for */
  metrics: MetricConfig[];
  /** Comparand Y-axis ID used as prefix in data keys (e.g., "comparand") */
  comparandYAxisId?: string;
  /** Comparator Y-axis ID used as prefix in data keys (e.g., "comparator") */
  comparatorYAxisId?: string;
  /** Value formatter for comparand (primary) axis */
  comparandValueFormatter?: (value: number | null) => string;
  /** Value formatter for comparator (secondary) axis */
  comparatorValueFormatter?: (value: number | null) => string;
  /** Direction where increase is positive for comparand: 'up' = green on increase, 'down' = green on decrease */
  comparandPositiveDirection?: 'up' | 'down';
  /** Direction where increase is positive for comparator: 'up' = green on increase, 'down' = green on decrease */
  comparatorPositiveDirection?: 'up' | 'down';
}

// ============================================
// Helper functions (pure, testable)
// ============================================

/**
 * Normalize selection to start/end indices.
 */
const normalizeSelection = (selection: SelectionRange): { startIndex: number; endIndex: number } | null => {
  if (!selection) return null;
  return {
    startIndex: selection.startIndex,
    endIndex: selection.endIndex,
  };
};

/**
 * Calculate variation for a single metric between start and end of selection.
 */
const calculateMetricVariation = (
  metric: MetricConfig,
  data: RechartsDataPoint[],
  range: { startIndex: number; endIndex: number },
  yAxisId?: string,
  isComparator: boolean = false
): MetricVariation => {
  // Build dataKey with yAxisId prefix to match chart data format
  const dataKey = yAxisId ? `${yAxisId}_${metric.key}` : metric.key;
  
  const startPoint = data[range.startIndex];
  const endPoint = data[range.endIndex];
  
  const startValue = startPoint?.[dataKey] as number | null ?? null;
  const endValue = endPoint?.[dataKey] as number | null ?? null;
  
  const delta = startValue !== null && endValue !== null 
    ? endValue - startValue 
    : null;
    
  const percentChange = startValue !== null && endValue !== null && startValue !== 0
    ? ((endValue - startValue) / Math.abs(startValue)) * 100
    : null;

  // Use comparatorVariationLabel for comparator, variationLabel for comparand
  const label = isComparator
    ? (metric.comparatorVariationLabel ?? metric.variationLabel ?? metric.label)
    : (metric.variationLabel ?? metric.label);

  return {
    key: metric.key,
    label,
    color: metric.color,
    startValue,
    endValue,
    delta,
    percentChange,
  };
};

/**
 * Calculate variations for all enabled metrics.
 */
const calculateAllVariations = (
  metrics: MetricConfig[],
  data: RechartsDataPoint[],
  range: { startIndex: number; endIndex: number },
  yAxisId?: string,
  isComparator: boolean = false
): MetricVariation[] => {
  return metrics
    .filter((m) => m.enabled)
    .map((metric) => calculateMetricVariation(metric, data, range, yAxisId, isComparator));
};

/**
 * Format a numeric value for display with 2 decimal places.
 */
const formatValue = (value: number | null): string => {
  if (value === null) return "-";
  return value.toLocaleString("it-IT", { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

/**
 * Format percentage change with sign and 2 decimal places.
 */
const formatPercentChange = (percent: number | null): string => {
  if (percent === null) return "-";
  const sign = percent > 0 ? "+" : "";
  return `${sign}${percent.toFixed(2)}%`;
};

/**
 * Format delta with sign.
 */
const formatDelta = (delta: number | null): string => {
  if (delta === null) return "-";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${formatValue(delta)}`;
};

// ============================================
// Sub-components
// ============================================

interface VariationChipProps {
  variation: MetricVariation;
  isComparator?: boolean;
  valueFormatter?: (value: number | null) => string;
  /** Direction where increase is positive: 'up' = green on increase, 'down' = green on decrease */
  positiveDirection?: 'up' | 'down';
}

const VariationChip = ({ variation, isComparator = false, valueFormatter, positiveDirection = 'up' }: VariationChipProps) => {
  const theme = useTheme();
  
  // Determine if the trend is "good" based on positiveDirection
  const trendColor = useMemo(() => {
    if (variation.delta === null) return theme.palette.text.secondary;
    const isIncrease = variation.delta > 0;
    const isDecrease = variation.delta < 0;
    // If positiveDirection is 'down', invert the color logic
    const isPositive = positiveDirection === 'down' ? isDecrease : isIncrease;
    const isNegative = positiveDirection === 'down' ? isIncrease : isDecrease;
    if (isPositive) return theme.palette.success.main;
    if (isNegative) return theme.palette.error.main;
    return theme.palette.text.secondary;
  }, [variation.delta, theme, positiveDirection]);

  const TrendIcon = useMemo(() => {
    if (variation.delta === null) return TrendingFlat;
    if (variation.delta > 0) return TrendingUp;
    if (variation.delta < 0) return TrendingDown;
    return TrendingFlat;
  }, [variation.delta]);

  // Add suffix for comparator metrics
  const displayLabel = isComparator ? `${variation.label} (comp.)` : variation.label;

  // Format delta using provided formatter or default
  const formattedDelta = useMemo(() => {
    if (variation.delta === null) return "-";
    const sign = variation.delta > 0 ? "+" : "";
    if (valueFormatter) {
      // For custom formatters, format the absolute value and add sign
      const formatted = valueFormatter(Math.abs(variation.delta));
      // Ensure numeric-looking strings are properly formatted to 2 decimals
      const numericValue = parseFloat(formatted);
      if (!isNaN(numericValue) && formatted === String(numericValue)) {
        // The formatter returned a raw number string, format it properly
        const properlyFormatted = numericValue.toLocaleString("it-IT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return variation.delta < 0 ? `-${properlyFormatted}` : `+${properlyFormatted}`;
      }
      return variation.delta < 0 ? `-${formatted}` : `+${formatted}`;
    }
    return `${sign}${formatValue(variation.delta)}`;
  }, [variation.delta, valueFormatter]);

  return (
    <Chip
      size="small"
      sx={{
        backgroundColor: variation.color 
          ? `${variation.color}20` 
          : theme.palette.action.hover,
        borderLeft: `3px ${isComparator ? 'dashed' : 'solid'} ${variation.color ?? theme.palette.divider}`,
        borderRadius: 1,
        height: "auto",
        py: 0.5,
      }}
      label={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" fontWeight="medium">
            {displayLabel}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TrendIcon sx={{ fontSize: 14, color: trendColor }} />
            <Typography 
              variant="caption" 
              fontWeight="bold"
              sx={{ color: trendColor }}
            >
              {formattedDelta}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ color: trendColor, opacity: 0.8 }}
            >
              ({formatPercentChange(variation.percentChange)})
            </Typography>
          </Stack>
        </Stack>
      }
    />
  );
};

// ============================================
// Main Component
// ============================================

export const BrushVariationSummary = ({
  selection,
  data,
  metrics,
  comparandYAxisId,
  comparatorYAxisId,
  comparandValueFormatter,
  comparatorValueFormatter,
  comparandPositiveDirection,
  comparatorPositiveDirection,
}: BrushVariationSummaryProps) => {
  const theme = useTheme();

  // Normalize selection to indices
  const range = useMemo(() => normalizeSelection(selection), [selection]);

  // Calculate variations for comparand (primary Y axis)
  const comparandVariations = useMemo(() => {
    if (!range || data.length === 0 || !comparandYAxisId) return [];
    return calculateAllVariations(metrics, data, range, comparandYAxisId, false);
  }, [range, data, metrics, comparandYAxisId]);

  // Calculate variations for comparator (secondary Y axis)
  const comparatorVariations = useMemo(() => {
    if (!range || data.length === 0 || !comparatorYAxisId) return [];
    return calculateAllVariations(metrics, data, range, comparatorYAxisId, true);
  }, [range, data, metrics, comparatorYAxisId]);

  // Get period labels for display
  const periodRange = useMemo(() => {
    if (!range || data.length === 0) return null;
    
    // Use labels from ChartSelection if available, otherwise extract from data
    if (selection && "startLabel" in selection) {
      return { start: selection.startLabel, end: selection.endLabel };
    }
    
    const startLabel = data[range.startIndex]?.date ?? "";
    const endLabel = data[range.endIndex]?.date ?? "";
    return { start: startLabel, end: endLabel };
  }, [selection, range, data]);

  // Don't render if no selection or no variations
  const hasVariations = comparandVariations.length > 0 || comparatorVariations.length > 0;
  if (!range || !hasVariations) return null;

  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: theme.palette.background.default,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack spacing={1}>
        {/* Header with period range (left) and title (right) */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {periodRange && (
            <Typography variant="caption" color="text.secondary">
              {periodRange.start} → {periodRange.end}
            </Typography>
          )}
          <Typography variant="subtitle2" color="text.secondary">
            Variazione nel periodo selezionato
          </Typography>
        </Stack>

        {/* Comparand variation chips */}
        {comparandVariations.length > 0 && (
          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap" 
            useFlexGap
          >
            {comparandVariations.map((v) => (
              <VariationChip 
                key={v.key} 
                variation={v} 
                valueFormatter={comparandValueFormatter}
                positiveDirection={comparandPositiveDirection}
              />
            ))}
          </Stack>
        )}

        {/* Comparator variation chips (with dashed border to distinguish) */}
        {comparatorVariations.length > 0 && (
          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap" 
            useFlexGap
            sx={{ 
              pt: 0.5,
              borderTop: `1px dashed ${theme.palette.divider}`,
            }}
          >
            {comparatorVariations.map((v) => (
              <VariationChip 
                key={`comp_${v.key}`} 
                variation={v} 
                isComparator 
                valueFormatter={comparatorValueFormatter}
                positiveDirection={comparatorPositiveDirection}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
